import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import betslip, { Betslip } from '@gin-betting/betslip';
import { App } from '@gin-betting/core';
import restrictions from '@gin-betting/restrictions';
import { createStructuredSelector } from 'reselect';
import type { Arg3 } from 'tsargs';

import { usePollingMinStake } from 'hooks';
import UserDevice from 'services/user-device';
import type { State } from 'types';
import { bindTrailingArgs } from 'utils';
import messages from '../messages';
import { RestrictionsContext } from '../restrictions';
import { selectBetByFirstBetslipOdd } from '../selectors';

const {
    selectors: { selectBetslipBetWithStatus, selectBetslipBetTotals },
    utils: { convertViewMaxStake, getErrorMsg: getErrorMsgByResponse },
} = betslip;

const {
    selectors: { selectLowerMaxBetBetslipBet },
} = restrictions;

type Output = {
    errorMsgs: Array<string | string[]>;
};

interface Input {
    betslipBetId: string;
    isFreebetSelected: boolean;
}

const useExpressSystemErrors = ({
    betslipBetId,
    isFreebetSelected,
}: Input): Output => {
    const errorMsgs: Array<string | string[]> = [];
    const { state: restrictionsState } = useContext(RestrictionsContext);

    const { formatMessage } = useIntl();

    const { betslipBetWithStatus, maxBet, bet } = useSelector((state: State) =>
        selectProps(state, { betslipBetId, restrictionsState })
    );

    const { minStake } = usePollingMinStake();

    if (!betslipBetWithStatus) return { errorMsgs };

    const { status, stake } = betslipBetWithStatus;

    const maxStake = isFreebetSelected || !maxBet ? '' : maxBet;

    const finalTotalStake = +stake;

    const isMinStakeError =
        !isFreebetSelected &&
        !!finalTotalStake &&
        !!minStake &&
        finalTotalStake < minStake;
    const isMaxStakeError =
        !isFreebetSelected && maxStake !== '' && finalTotalStake > +maxStake;

    const isMaxLessMinStakeError =
        !isFreebetSelected && !!minStake && !!maxStake && +maxStake < minStake;

    const isMobile = UserDevice.getInstance()?.isDeviceMobile();

    if (isMaxLessMinStakeError) {
        errorMsgs.push([
            `${formatMessage(messages.attention)}!`,
            formatMessage(messages.betslipOddsHasConflicts),
        ]);
    } else {
        if (isMinStakeError) {
            const minStakeError: string[] = [
                `${formatMessage(messages.stakeMinimalStakeIs)} ${minStake}`,
            ];

            if (!isMobile) {
                minStakeError.unshift(`${formatMessage(messages.attention)}!`);
            }

            errorMsgs.push(minStakeError);
        }

        if (isMaxStakeError) {
            const maxStakeError: string[] = [
                `${formatMessage(messages.errorMaxStake)} ${convertViewMaxStake(
                    maxStake
                )}`,
            ];

            if (!isMobile) {
                maxStakeError.unshift(`${formatMessage(messages.attention)}!`);
            }

            errorMsgs.push(maxStakeError);
        }
    }

    if (status === App.BetslipStatus.ERROR) {
        errorMsgs.push([
            `${formatMessage(messages.attention)}!`,
            getErrorMsgByResponse(
                bet?.declineReason as Betslip.ErrorCodes | undefined,
                bindTrailingArgs(formatMessage, {
                    host: window.location.host,
                }),
                messages as Arg3<typeof getErrorMsgByResponse>
            ),
        ]);
    }

    return {
        errorMsgs,
    };
};

const selectProps = createStructuredSelector({
    betslipBetWithStatus: selectBetslipBetWithStatus,
    totals: selectBetslipBetTotals,
    maxBet: selectLowerMaxBetBetslipBet,
    bet: selectBetByFirstBetslipOdd,
});

export default useExpressSystemErrors;
